import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useListState } from '../../lib/hooks';
import { date, money, num, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { EmptyState, ErrorState, PageHead } from '../../components/ui';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import { coachFor, type LostCoachTip } from '@goyone/shared';
import { OwnerChip } from '../../components/People';
import {
  PeriodNav,
  spanQuery,
  type Grain,
  type Span,
} from '../../components/PeriodNav';
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type SectionDef,
} from '../../components/ProfileLayout';

interface LostData {
  total: { count: number; value: number };
  byReason: Array<{ reason: string; count: number; value: number; share: number }>;
  byStage: Array<{ stage: string; label: string; count: number; value: number }>;
  byOwner: Array<{ ownerId: string | null; count: number; value: number }>;
  bySource: Array<{
    source: string;
    lost: number;
    won: number;
    lostValue: number;
    lossRate: number | null;
  }>;
  byService: Array<{ name: string; count: number; value: number }>;
  recent: Array<{
    id: string;
    title: string;
    value: string;
    lostReason: string | null;
    closedAt: string | null;
    stage: string;
    account: { id: string; name: string } | null;
  }>;
  periodDays: number;
  label: string;
  trendRows: Array<{ key: string; label: string; count: number; value: number }>;
  byValueBand: Array<{
    band: string;
    lost: number;
    won: number;
    lostValue: number;
    lossRate: number | null;
  }>;
  compare: {
    avgLostValue: number;
    avgWonValue: number;
    daysToLose: number | null;
    daysToWin: number | null;
    earlyCount: number;
    earlyValue: number;
    lateCount: number;
    lateValue: number;
  };
  insights: {
    wonCount: number;
    wonValue: number;
    lossRate: number | null;
    averageLostValue: number;
    averageDaysToLose: number | null;
    lateStageValue: number;
    lateStageCount: number;
    unlabeledCount: number;
    previous: { count: number; value: number };
    trend: number | null;
  };
}
/**
 * أقسامُ الشاشة.
 *
 * ما لا يُقسَم بعرضها: النصائحُ لأنّها الإجراء، وخطُّ الخسارة لأنّه اثنتا
 * عشرة شريحة، والأسبابُ لأنّها أشرطةٌ تُقارَن بأطوالها، والسجلُّ لأنّه
 * جدولٌ بخمسة أعمدة. وما بينهما يُقرأ قرينًا في عمودين.
 */
const LOSS_SECTIONS: SectionDef[] = [
  { key: 'vitals', title: 'الخسارة في سطر', column: 'full' },
  { key: 'where', title: 'أين تقع الخسارة', column: 'main' },
  { key: 'size', title: 'أنخسر الكبير أم الصغير', column: 'side' },
  { key: 'coach', title: 'ماذا تفعل لتقليل الخسائر', column: 'full' },
  { key: 'trend', title: 'خطّ الخسارة', column: 'full' },
  { key: 'reasons', title: 'أسباب الخسارة', column: 'full' },
  { key: 'stages', title: 'المرحلة التي توقفت عندها', column: 'main' },
  { key: 'sources', title: 'الخسائر حسب المصدر', column: 'side' },
  { key: 'owners', title: 'الخسائر حسب المندوب', column: 'main' },
  { key: 'services', title: 'خدماتٌ عُرضت ولم تُقبل', column: 'side' },
  { key: 'recent', title: 'آخر الصفقات الخاسرة', column: 'full' },
];

export function LostAnalysis() {
  const { state, patch } = useListState({ bucket: 'year', offset: '0' });
  const layout = useProfileLayout('goyone.layout.lost', LOSS_SECTIONS);

  /*
   * مدى الصفحة — كما في القمع سواء.
   *
   * «آخر مئة وثمانين يومًا» مدّةٌ متدحرجة لا تُقارَن بشيء: لا بشهرٍ بعينه
   * ولا بربعٍ ولا بسنة. وقراءةُ الموقف مقارنةٌ قبل كلّ شيء.
   */
const master: LossSpan = {
    grain: (state.bucket as Grain | undefined) ?? null,
    offset: Number(state.offset) || 0,
    from: state.from,
    to: state.to,
  };
  const setMaster = (g: Grain | null, o: number) =>
    patch({
      bucket: g ?? undefined,
      offset: String(o),
      from: undefined,
      to: undefined,
    });

  const lost = useLostQuery(master);

  if (lost.isError) {
    return (
      <div className="page">
        <ErrorState error={lost.error} onRetry={() => void lost.refetch()} />
      </div>
    );
  }

  const data = lost.data;
  const maxReason = Math.max(1, ...(data?.byReason ?? []).map((r) => r.count));
  const maxStage = Math.max(1, ...(data?.byStage ?? []).map((s) => s.count));
  const maxOwner = Math.max(1, ...(data?.byOwner ?? []).map((x) => x.value));
  const maxService = Math.max(1, ...(data?.byService ?? []).map((x) => x.value));

  return (
    <div className="page">
      <PageHead
        title="تحليل الخسائر"
        subtitle="لماذا تُفقد الصفقات، وأين تتوقف"
        actions={
          <>
            <PeriodNav
              grain={master.grain}
              onGrain={(g) => setMaster(g, 0)}
              offset={master.offset}
              onOffset={(o) => setMaster(master.grain, o)}
              label={data?.label ?? ''}
              range={{ from: master.from, to: master.to }}
              onRange={(from, to) => patch({ from, to, offset: '0' })}
              /*
               * و«الكل» أصلُ هذه الشاشة لا استثناؤها: «أين نخسر عادةً؟» لا
               * يُجاب بشهر — الأنماطُ تظهر على التاريخ كلِّه، والفترةُ
               * تضيّقها حتى تصير ثلاثَ صفقاتٍ لا يُبنى عليها حكم.
               */
              withAll
            />
            <ProfileLayoutButton api={layout} />
            <Link to="/sales/pipeline" className="btn">
              <Icon name="funnel" size={15} />
              القمع
            </Link>
          </>
        }
      />

      {data && (
        <RecordStats
          items={lostCards(data)}
          storageKey="goyone.stats.lost"
          defaults={['count', 'value', 'lossRate', 'topReason', 'lateStage', 'trend']}
        />
      )}

      {data?.total.count === 0 ? (
        <div className="card">
          <EmptyState
            title="لا توجد صفقات خاسرة في هذه الفترة"
            text="عند إغلاق صفقة كخاسرة يُطلب سبب الخسارة، وتظهر هنا مجمّعة."
          />
        </div>
      ) : (
        <ProfileLayoutProvider api={layout}>
          <ProfileLayoutBar api={layout} />

          <LayoutColumn api={layout} column="full">
            {/*
              الأرقامُ الحيّة أوّلًا: نسبةُ الخسارة وقيمتُها، والخاسرُ في
              مرآة الفائز قيمةً وزمنًا. ثمّ ما يُفعل بها.
            */}
            <Section layoutKey="vitals">
              <LossVitals master={master} />
            </Section>

            {/* النصائح: الإجراءُ أهمّ ما يقلّل الخسائر، فلا يُدفن في الوسط. */}
            <Section layoutKey="coach">
              <LostCoach master={master} />
            </Section>

            <Section layoutKey="trend">
              <LossTrend master={master} />
            </Section>

            {/*
              الأسباب أولًا لأنها المعلومة القابلة للتصرف.
              «السعر مرتفع» يقود إلى مراجعة التسعير، و«انقطع التواصل» يقود
              إلى مراجعة المتابعة. وهما إجراءان مختلفان تمامًا.
            */}
            <Section layoutKey="reasons">
              <ReasonsCard master={master} />
            </Section>
          </LayoutColumn>

          <div className="two-col pl-two">
            <LayoutColumn api={layout} column="main">
              <Section layoutKey="where">
                <LossWhere master={master} />
              </Section>

              <Section layoutKey="stages">
                <StagesCard master={master} />
              </Section>

              <Section layoutKey="owners">
                <OwnersCard master={master} />
              </Section>
            </LayoutColumn>

            <LayoutColumn api={layout} column="side">
              <Section layoutKey="size">
                <LossBySize master={master} />
              </Section>

              <Section layoutKey="sources">
                <SourcesCard master={master} />
              </Section>

              <Section layoutKey="services">
                <ServicesCard master={master} />
              </Section>
            </LayoutColumn>
          </div>

          <LayoutColumn api={layout} column="full">
            <Section layoutKey="recent">
              <RecentCard master={master} />
            </Section>
          </LayoutColumn>
        </ProfileLayoutProvider>
      )}
    </div>
  );
}

/**
 * مدى الخسائر — كالمدى في القمع، وفيه «الكل».
 *
 * سؤالُ «أين نخسر عادةً؟» لا يُجاب بشهرٍ ولا بسنة: الأنماطُ تظهر على
 * التاريخ كلِّه، والفترةُ تضيّقها حتى تصير ثلاثَ صفقاتٍ لا يُبنى عليها
 * حكم. فـ«الكل» أصلٌ لا استثناء، والفترةُ تُطلب لمن أرادها.
 */
interface LossSpan {
  grain: Grain | null;
  offset: number;
  from?: string;
  to?: string;
}

const lossQuery = (sp: LossSpan) =>
  sp.from && sp.to
    ? { from: sp.from, to: sp.to }
    : sp.grain
      ? { bucket: sp.grain, offset: sp.offset }
      : {};

function useLostQuery(span: LossSpan) {
  return useQuery({
    queryKey: ['lost-analysis', lossQuery(span)],
    queryFn: () =>
      api<LostData>(`/sales/deals/lost-analysis${qs(lossQuery(span))}`),
    placeholderData: (prev) => prev,
  });
}

/**
 * بطاقةٌ بمداها.
 *
 * كلُّ بطاقةٍ تتبع مدى الرأس ما لم تنفرد بمداها. والمفتاحُ واحدٌ للطلب،
 * فبطاقاتٌ على مدًى واحدٍ تشترك في طلبٍ واحد — والانفرادُ وحدَه يكلّف
 * طلبًا ثانيًا.
 */
function useLostCard(master: LossSpan) {
  const [own, setOwn] = useState<LossSpan | null>(null);
  useEffect(() => {
    setOwn(null);
  }, [master.grain, master.offset, master.from, master.to]);

  const span = own ?? master;
  const q = useLostQuery(span);

  return {
    span,
    data: q.data,
    nav: {
      grain: span.grain,
      onGrain: (g: Grain | null) =>
        setOwn({ grain: g, offset: 0, from: undefined, to: undefined }),
      offset: span.offset,
      onOffset: (o: number) => setOwn({ ...span, offset: o }),
      range: { from: span.from, to: span.to },
      onRange: (from?: string, to?: string) =>
        setOwn({ grain: span.grain, offset: 0, from, to }),
      withAll: true,
    },
  };
}

/** رأسُ بطاقةٍ بعنوانه وفلتره — واحدٌ لكلّ بطاقات الصفحة. */
function CardHead({
  title,
  note,
  card,
}: {
  title: string;
  note?: string;
  card: ReturnType<typeof useLostCard>;
}) {
  return (
    <div className="card-head">
      <h2 className="card-title">{title}</h2>
      <PeriodNav {...card.nav} label={card.data?.label ?? ''} />
      {note && <span className="tiny mute">{note}</span>}
    </div>
  );
}

/** أسبابُ الخسارة — المعلومةُ الوحيدة القابلة للتصرّف مباشرةً. */
function ReasonsCard({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.byReason ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 220 }} />;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="card mb-4">
      <CardHead title="أسباب الخسارة" note="ما يُسجَّل عند الإغلاق" card={card} />
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="tiny mute center">لا أسباب مسجَّلة.</p>
        ) : (
          <div className="col" style={{ gap: 'var(--sp-3)' }}>
            {rows.map((r) => (
              <div key={r.reason}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="small strong">{r.reason}</span>
                  <span className="small mute">
                    <span className="num">{num(r.count)}</span> ·{' '}
                    <span className="num">{r.share}٪</span> — {money(r.value)}
                  </span>
                </div>
                <div className="bar-track mt-2">
                  <span
                    className="bar-fill"
                    style={{
                      width: `${(r.count / max) * 100}%`,
                      background: 'var(--danger)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** المرحلةُ التي توقفت عندها — التكرارُ في مرحلةٍ يشير إلى خللٍ فيها. */
function StagesCard({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.byStage ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 220 }} />;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="card mb-4">
      <CardHead title="المرحلة التي توقفت عندها" card={card} />
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="tiny mute center">لا بيانات.</p>
        ) : (
          <div className="col" style={{ gap: 'var(--sp-3)' }}>
            {rows.map((r) => (
              <div key={r.stage}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="small">{r.label}</span>
                  <span className="small mute num">{num(r.count)}</span>
                </div>
                <div className="bar-track mt-2">
                  <span
                    className="bar-fill"
                    style={{
                      width: `${(r.count / max) * 100}%`,
                      background: 'var(--warning)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="tiny mute mt-4">
          التوقف المتكرر في مرحلة واحدة يشير إلى خلل في تلك الخطوة تحديدًا.
        </p>
      </div>
    </div>
  );
}

/** الخسائرُ حسب المندوب — لتدريبٍ موجَّه لا لومٍ عام. */
function OwnersCard({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.byOwner ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 200 }} />;
  const max = Math.max(1, ...rows.map((x) => x.value));
  return (
    <div className="card mb-4">
      <CardHead
        title="الخسائر حسب المندوب"
        note="لتدريبٍ موجَّه لا لومٍ عام"
        card={card}
      />
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="tiny mute center">لا بيانات.</p>
        ) : (
          <div className="col" style={{ gap: 'var(--sp-3)' }}>
            {[...rows]
              .sort((a, b) => b.value - a.value)
              .map((o) => (
                <div key={o.ownerId ?? 'none'}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    {o.ownerId ? (
                      <OwnerChip ownerId={o.ownerId} size={22} />
                    ) : (
                      <span className="small mute">بلا مسؤول</span>
                    )}
                    <span className="small mute">
                      <span className="num">{num(o.count)}</span> — {money(o.value)}
                    </span>
                  </div>
                  <div className="bar-track mt-2">
                    <span
                      className="bar-fill"
                      style={{
                        width: `${(o.value / max) * 100}%`,
                        background: 'var(--danger)',
                      }}
                    />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** جودةُ المصادر — بنسبة الخسارة لا بعددها. */
function SourcesCard({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.bySource ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 200 }} />;
  return (
    <div className="card mb-4">
      <CardHead
        title="الخسائر حسب المصدر"
        note="بنسبة الخسارة لا بعددها"
        card={card}
      />
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="tiny mute center">لا بيانات.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>المصدر</th>
                  <th className="col-num">خسائر</th>
                  <th className="col-num">نسبتها</th>
                  <th className="col-num">قيمتها</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.source}>
                    <td className="cell-strong">{r.source}</td>
                    <td className="num">{num(r.lost)}</td>
                    <td className="num">
                      {/* النسبة لا العدد: الأكبرُ حجمًا يخسر أكثر بطبعه. */}
                      <span
                        style={{
                          color:
                            r.lossRate != null && r.lossRate >= 60
                              ? 'var(--danger)'
                              : undefined,
                        }}
                      >
                        {r.lossRate == null ? '—' : `${r.lossRate}٪`}
                      </span>
                    </td>
                    <td className="num cell-mute">{money(r.lostValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="tiny mute mt-4">
          المصدر ما يُدخله الفريق في الصفقة، لا مسحوبٌ من منصّات الإعلانات.
        </p>
      </div>
    </div>
  );
}

/** خدماتٌ عُرضت ولم تُقبل — من بنود العروض المرفوضة والمنتهية. */
function ServicesCard({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.byService ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 200 }} />;
  const max = Math.max(1, ...rows.map((x) => x.value));
  return (
    <div className="card mb-4">
      <CardHead
        title="خدماتٌ عُرضت ولم تُقبل"
        note="من بنود العروض المرفوضة والمنتهية"
        card={card}
      />
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="tiny mute center">لا بيانات.</p>
        ) : (
          <div className="col" style={{ gap: 'var(--sp-3)' }}>
            {rows.map((r) => (
              <div key={r.name}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="small strong">{r.name}</span>
                  <span className="small mute">
                    <span className="num">{num(r.count)}</span> — {money(r.value)}
                  </span>
                </div>
                <div className="bar-track mt-2">
                  <span
                    className="bar-fill"
                    style={{
                      width: `${(r.value / max) * 100}%`,
                      background: 'var(--warning)',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** آخرُ الصفقات الخاسرة — السجلُّ الذي تُراجَع عليه الأرقام. */
function RecentCard({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.recent ?? [];
  if (!card.data) return <div className="card skeleton" style={{ height: 240 }} />;
  return (
    <div className="card">
      <CardHead title="آخر الصفقات الخاسرة" card={card} />
      <div className="table-wrap sticky-head">
        <table className="table">
          <thead>
            <tr>
              <th>الصفقة</th>
              <th>الحساب</th>
              <th className="col-num">القيمة</th>
              <th>السبب</th>
              <th>تاريخ الإغلاق</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td className="cell-strong">{d.title}</td>
                <td>
                  {d.account ? (
                    <Link to={`/crm/accounts/${d.account.id}`}>{d.account.name}</Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="num">{money(d.value)}</td>
                <td className="cell-mute">{d.lostReason ?? 'بلا سبب مسجَّل'}</td>
                <td className="cell-mute">{date(d.closedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * الخسارة في سطر — نسبتُها، وقيمتُها، والخاسرُ في مرآة الفائز.
 *
 * الخسارةُ وحدها لا تُقاس. «خمسمئة ألف» لا تقول شيئًا حتى يُعرف كم رُبح
 * بإزائها، وكم كانت الصفقةُ الخاسرة أكبرَ أو أصغرَ من الفائزة، وكم أخذت
 * من الوقت قبل أن تسقط. فثلاثُ مقارناتٍ لا ثلاثةُ أرقامٍ معزولة.
 */
function LossVitals({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const data = card.data;
  if (!data) return <div className="card mb-4 skeleton" style={{ height: 190 }} />;
  const i = data.insights;
  const c = data.compare;
  const rate = i.lossRate ?? 0;

  /* الفارقُ في القيمة: أنخسر أكبرَ ممّا نربح؟ */
  const valueGap =
    c.avgWonValue > 0
      ? Math.round(((c.avgLostValue - c.avgWonValue) / c.avgWonValue) * 100)
      : null;
  /* والفارقُ في الزمن: أتموت الصفقةُ بطيئةً فتأكل الطاقة؟ */
  const timeGap =
    c.daysToWin != null && c.daysToLose != null ? c.daysToLose - c.daysToWin : null;

  return (
    <div className="card mb-4">
      <CardHead title="الخسارة في سطر" card={card} />
      <div className="card-body">
        <div className="lv">
          <span
            className="hz-ring lv-ring"
            style={{
              ['--hz-pct' as string]: `${Math.min(100, rate)}%`,
              ['--hz-tone' as string]: 'var(--danger)',
            }}
            role="img"
            aria-label={`نسبة الخسارة ${rate} بالمئة`}
          >
            <span className="hz-ring-in">
              <b className="num hz-count">{i.lossRate == null ? '—' : `${rate}٪`}</b>
              <span className="hz-share">خسارة</span>
            </span>
          </span>

          <div className="lv-figs">
            <div className="db-kpi">
              <span className="db-kpi-label">القيمة المفقودة</span>
              <span className="db-kpi-value num" style={{ color: 'var(--danger)' }}>
                {money(data.total.value)}
              </span>
              <span className="db-kpi-hint">
                {plural(data.total.count, WORDS.deal)} مقابل{' '}
                {plural(i.wonCount, WORDS.deal)} ناجحة
              </span>
            </div>

            <div className="db-kpi">
              <span className="db-kpi-label">متوسط الصفقة الخاسرة</span>
              <span className="db-kpi-value num">{money(c.avgLostValue)}</span>
              <span className="db-kpi-hint">
                {valueGap == null
                  ? `الفائزة ${money(c.avgWonValue)}`
                  : valueGap > 0
                    ? `أكبرُ من الفائزة بـ${valueGap}٪ — نخسر ما هو أثمن`
                    : `أصغرُ من الفائزة بـ${Math.abs(valueGap)}٪`}
              </span>
            </div>

            <div className="db-kpi">
              <span className="db-kpi-label">عمر الصفقة الخاسرة</span>
              <span className="db-kpi-value num">
                {c.daysToLose == null ? '—' : plural(c.daysToLose, WORDS.day)}
              </span>
              <span className="db-kpi-hint">
                {timeGap == null
                  ? 'لا مقارنة بعد'
                  : timeGap > 0
                    ? `أطولُ من الفائزة بـ${plural(timeGap, WORDS.day)} — تأكل الطاقة قبل أن تسقط`
                    : `أقصرُ من الفائزة بـ${plural(Math.abs(timeGap), WORDS.day)}`}
              </span>
            </div>

            <div className="db-kpi">
              <span className="db-kpi-label">بلا سببٍ مسجَّل</span>
              <span
                className="db-kpi-value num"
                style={{ color: i.unlabeledCount > 0 ? 'var(--warning)' : undefined }}
              >
                {num(i.unlabeledCount)}
              </span>
              <span className="db-kpi-hint">
                {i.unlabeledCount > 0
                  ? 'لا تُدرَس ولا تُصلَح — سجّل السبب عند الإغلاق'
                  : 'كلُّها مسجَّلة'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * أين تقع الخسارة من القمع — قبل العرض أم بعده.
 *
 * أهمُّ سؤالٍ في تشخيص الخسارة، وأرخصُه جوابًا. الخسارةُ المبكّرة رخيصة:
 * عميلٌ لم يكن مؤهَّلًا أصلًا، وعلاجُها في التأهيل. والمتأخّرةُ باهظة: عرضٌ
 * كُتب ووقتٌ أُنفق وتفاوضٌ جرى ثمّ سقط — وعلاجُها في التسعير أو المتابعة.
 * وخلطُهما في رقمٍ واحد يجعل العلاجَ عشوائيًّا.
 */
function LossWhere({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const data = card.data;
  if (!data) return <div className="card mb-4 skeleton" style={{ height: 220 }} />;
  const c = data.compare;
  const total = c.earlyValue + c.lateValue || 1;
  const parts = [
    {
      key: 'late',
      label: 'بعد العرض',
      hint: 'باهظة — بعد استثمار جهد',
      count: c.lateCount,
      value: c.lateValue,
      tone: 'var(--danger)',
    },
    {
      key: 'early',
      label: 'قبل العرض',
      hint: 'رخيصة — علاجُها في التأهيل',
      count: c.earlyCount,
      value: c.earlyValue,
      tone: 'var(--warning)',
    },
  ];
  const latePct = Math.round((c.lateValue / total) * 100);

  return (
    <div className="card mb-4">
      <CardHead title="أين تقع الخسارة" note="بالقيمة لا بالعدد" card={card} />
      <div className="card-body">
        <div className="cv-split" style={{ borderTop: 0, paddingTop: 0 }}>
          <span className="cv-bar">
            {parts
              .filter((p) => p.value > 0)
              .map((p) => (
                <span
                  key={p.key}
                  className="cv-seg"
                  style={{ width: `${(p.value / total) * 100}%`, background: p.tone }}
                  title={`${p.label} — ${plural(p.count, WORDS.deal)} بقيمة ${money(p.value)}`}
                />
              ))}
          </span>
          <span className="cv-key">
            {parts.map((p) => (
              <span key={p.key} className="cv-key-item">
                <span className="dot" style={{ background: p.tone }} aria-hidden />
                {p.label}
                <b className="num">{money(p.value)}</b>
              </span>
            ))}
          </span>
        </div>

        <div className="col mt-3" style={{ gap: 4 }}>
          {parts.map((p) => (
            <span key={p.key} className="tiny mute">
              <strong>{p.label}:</strong> {p.hint} — {plural(p.count, WORDS.deal)}
            </span>
          ))}
        </div>

        <p className="tiny mute mt-3">
          {c.lateValue === 0 && c.earlyValue === 0
            ? 'لا خسائر في هذه الفترة.'
            : latePct >= 60
              ? `${latePct}٪ من قيمتك المفقودة تسقط بعد العرض — الخلل في التسعير أو في المتابعة بعد الإرسال، لا في التأهيل.`
              : `${100 - latePct}٪ من قيمتك المفقودة تسقط قبل العرض — الخلل في التأهيل: يدخل القمعَ من لا يشتري.`}
        </p>
      </div>
    </div>
  );
}

/**
 * أنخسر الكبيرَ أم الصغير؟
 *
 * شركةٌ تخسر عشرين صفقةً صغيرةً غيرُ شركةٍ تخسر اثنتين كبيرتين، والعلاجُ
 * مختلف. والأرباعُ تُشتقّ من أرقام الشركة نفسها لا من حدودٍ مخترعة.
 *
 * ولونٌ واحدٌ لا أربعة: هذه سلسلةٌ واحدة — نسبةُ الخسارة — والارتفاعُ هو
 * المعنى، فالألوانُ فيها زينةٌ تُشوّش لا هويّةٌ تُعرِّف.
 */
function LossBySize({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.byValueBand ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 220 }} />;
  const has = rows.some((r) => r.lost + r.won > 0);
  return (
    <div className="card mb-4">
      <CardHead
        title="أنخسر الكبير أم الصغير"
        note="نسبة الخسارة بحجم الصفقة"
        card={card}
      />
      <div className="card-body">
        {!has ? (
          <p className="tiny mute center">لا صفقات محسومة في هذه الفترة.</p>
        ) : (
          <>
            <div className="sz">
              {rows.map((r) => (
                <div className="sz-col" key={r.band}>
                  <span className="sz-bar-wrap">
                    <span
                      className="sz-bar"
                      style={{ height: `${r.lossRate ?? 0}%` }}
                      title={`${r.band} — خُسر ${num(r.lost)} من ${num(r.lost + r.won)} بقيمة ${money(r.lostValue)}`}
                    />
                  </span>
                  <span className="num sz-pct">
                    {r.lossRate == null ? '—' : `${r.lossRate}٪`}
                  </span>
                  <span className="tiny mute sz-band">{r.band}</span>
                </div>
              ))}
            </div>
            <p className="tiny mute mt-3">
              ارتفاعُ النسبة كلّما كبرت الصفقة يعني أنّ الحجم نفسه هو
              العائق — تسعيرٌ أو ثقةٌ أو دورةُ قرارٍ أطول لا يحتملها متابعتُك
              الحالية.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * خطُّ الخسارة داخل الفترة.
 *
 * رقمٌ واحدٌ للفترة كلّها يخفي أسبوعًا انهار وآخرَ هدأ. والفترةُ حرّةُ
 * الطول — أسبوعٌ أو سنةٌ أو مدًى مكتوب — فتُقسَم اثنتي عشرة شريحةً متساوية
 * مهما طالت، ويُقرأ الاتجاهُ في كلّ الأحوال بالمقياس نفسه.
 *
 * والعمودُ ارتفاعُه بالقيمة المفقودة، والعددُ في تلميحه: خسارةُ صفقةٍ
 * كبيرةٍ ليست كخسارة خمسٍ صغيرة، والقيمةُ هي ما يُوجع.
 */
function LossTrend({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const rows = card.data?.trendRows ?? [];
  if (!card.data) return <div className="card mb-4 skeleton" style={{ height: 240 }} />;
  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((n, r) => n + r.value, 0);
  const peak = rows.reduce((top, r) => (r.value > top.value ? r : top), rows[0]);

  return (
    <div className="card mb-4">
      <CardHead title="خطّ الخسارة" card={card} />
      <div className="card-body">
        <div className="lt-chart">
          {rows.map((r) => (
            <div className="lt-col" key={r.key}>
              <span className="lt-bar-wrap">
                <span
                  className="lt-bar"
                  style={{ height: `${(r.value / max) * 100}%` }}
                  title={`${r.label} — ${plural(r.count, WORDS.deal)} بقيمة ${money(r.value)}`}
                />
              </span>
              <span className="lt-x num">{r.label}</span>
            </div>
          ))}
        </div>
        <div className="lt-foot">
          <span>
            المفقود في الفترة <b className="num">{money(total)}</b>
          </span>
          {peak && peak.value > 0 && (
            <span className="tiny mute">
              أثقل شريحة {peak.label} — <span className="num">{money(peak.value)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   البطاقات والنصائح
   ========================================================================== */

/**
 * بطاقات الخسارة.
 *
 * الخسارة وحدها لا تُقاس — تُقاس بنسبتها إلى الفوز، وبعمقها في القمع
 * (خسارةٌ في التفاوض أثقل من خسارةٍ في التأهيل لأنها بعد استثمار جهد)،
 * وباتجاهها مقارنةً بالمدة السابقة.
 */
function lostCards(d: LostData): RecordStat[] {
  const i = d.insights;
  const top = d.byReason[0];
  return [
    { key: 'count', label: 'صفقات خاسرة', group: 'الحجم', icon: 'alert', color: '#DC2626', value: num(d.total.count), numeric: true, note: `في ${d.periodDays} يومًا` },
    { key: 'value', label: 'القيمة المفقودة', group: 'الحجم', icon: 'wallet', color: '#DC2626', value: money(d.total.value), numeric: true },
    { key: 'avgValue', label: 'متوسط الصفقة المفقودة', group: 'الحجم', icon: 'receipt', color: '#8791A1', value: money(i.averageLostValue), numeric: true },
    { key: 'lossRate', label: 'نسبة الخسارة', group: 'الأداء', icon: 'chartBar', color: i.lossRate != null && i.lossRate > 50 ? '#DC2626' : '#BF930B', value: i.lossRate != null ? `${i.lossRate}٪` : '—', numeric: true, note: `مقابل ${num(i.wonCount)} ناجحة`, hint: 'الخاسرة من مجموع المُغلق في المدة.' },
    { key: 'trend', label: 'الاتجاه', group: 'الأداء', icon: 'zap', color: i.trend != null && i.trend > 0 ? '#DC2626' : '#51A521', value: i.trend != null ? `${i.trend > 0 ? '+' : ''}${i.trend}٪` : '—', numeric: true, note: `السابقة ${num(i.previous.count)}`, hint: 'مقارنةً بالمدة السابقة المماثلة — الموجب يعني ازديادًا.' },
    { key: 'lateStage', label: 'خسائر متأخرة', group: 'العمق', icon: 'funnel', color: '#BF930B', value: money(i.lateStageValue), numeric: true, note: `${num(i.lateStageCount)} صفقة بعد العرض`, hint: 'خسارةٌ بعد العرض أو التفاوض — أثقل لأنها بعد استثمار جهد.' },
    { key: 'daysToLose', label: 'متوسط عمر الصفقة الخاسرة', group: 'العمق', icon: 'clock', color: '#5596FF', value: i.averageDaysToLose != null ? `${i.averageDaysToLose} يومًا` : '—', numeric: true, hint: 'من الإنشاء إلى الإغلاق بخسارة.' },
    { key: 'topReason', label: 'السبب الأكثر تكرارًا', group: 'الأسباب', icon: 'inbox', color: '#3672EA', value: top?.reason ?? 'لا بيانات', note: top ? `${top.share}٪ من الخسائر` : undefined },
    { key: 'unlabeled', label: 'بلا سببٍ مسجَّل', group: 'الأسباب', icon: 'x', color: i.unlabeledCount > 0 ? '#BF930B' : '#9BA3AE', value: num(i.unlabeledCount), numeric: true, note: i.unlabeledCount > 0 ? 'لا تُدرَس ولا تُصلَح' : 'كلها مسجَّلة', hint: 'الخسارة بلا سبب لا يمكن تحليلها.' },
  ];
}

/**
 * نصائح تقليل الخسائر.
 *
 * تُبنى من أسباب هذه الشركة فعلًا لا من قائمةٍ عامة: أكثر ثلاثة أسبابٍ
 * تكرارًا يُطابَق كلٌّ منها بنمطٍ معروف فتُعرض أفعاله. ويُضاف تنبيهان
 * يُقرآن من الأرقام مباشرة: الخسائر المتأخرة، والخسائر بلا سبب.
 */
function LostCoach({ master }: { master: LossSpan }) {
  const card = useLostCard(master);
  const data = card.data;
  if (!data) return <div className="card mb-4 skeleton" style={{ height: 240 }} />;
  const i = data.insights;

  /* أكثر ثلاثة أسبابٍ — بلا تكرار نمطٍ واحد مرّتين. */
  const seen = new Set<string>();
  const tips: Array<{ reason: string; share: number; tip: LostCoachTip }> = [];
  for (const r of data.byReason) {
    const tip = coachFor(r.reason);
    if (seen.has(tip.key)) continue;
    seen.add(tip.key);
    tips.push({ reason: r.reason, share: r.share, tip });
    if (tips.length >= 3) break;
  }

  const alerts: string[] = [];
  if (i.lateStageCount > 0 && i.lateStageValue > data.total.value * 0.4) {
    alerts.push(
      `أكثر من ٤٠٪ من قيمتك المفقودة تسقط بعد العرض (${money(i.lateStageValue)}). راجع مرحلة التفاوض: العرض يصل ثم يتوقف الحديث.`,
    );
  }
  if (i.unlabeledCount > 0) {
    alerts.push(
      `${num(i.unlabeledCount)} صفقة أُغلقت بلا سببٍ مسجَّل — لا يمكن تحليلها. سجّل السبب عند كل إغلاق.`,
    );
  }
  if (i.trend != null && i.trend > 20) {
    alerts.push(
      `الخسائر ارتفعت ${i.trend}٪ عن المدة السابقة. راجع ما تغيّر: التسعير، أو المنافسة، أو انضباط المتابعة.`,
    );
  }
  if (i.averageDaysToLose != null && i.averageDaysToLose > 60) {
    alerts.push(
      `الصفقة الخاسرة تبقى مفتوحةً ${i.averageDaysToLose} يومًا وسطيًّا. الإغلاق المبكر يحرّر وقتك لصفقةٍ أقرب.`,
    );
  }

  return (
    <div className="card mb-4 lost-coach">
      <CardHead
        title="ماذا تفعل لتقليل الخسائر"
        note="مبنيّ على أسبابك أنت، لا نصائح عامة"
        card={card}
      />
      <div className="card-body">
        {alerts.length > 0 && (
          <div className="col mb-4" style={{ gap: 'var(--sp-2)' }}>
            {alerts.map((a) => (
              <div key={a} className="alert alert-warning">
                <Icon name="alert" size={15} /> {a}
              </div>
            ))}
          </div>
        )}

        {tips.length === 0 ? (
          <p className="mute center tiny">لا أسباب مسجَّلة بعد.</p>
        ) : (
          <div className="coach-grid">
            {tips.map(({ reason, share, tip }, i) => (
              <div
                key={tip.key}
                className="coach-card"
                style={{ ['--i' as string]: i }}
              >
                <div className="coach-head">
                  <strong>{tip.title}</strong>
                  <span className="tiny mute">
                    {reason} — {share}٪ من الخسائر
                  </span>
                </div>

                {/* نصيب السبب مرسومًا: حجم المشكلة يُرى قبل أن يُقرأ. */}
                <div
                  className="coach-share"
                  role="img"
                  aria-label={`${share}٪ من الخسائر`}
                >
                  <span style={{ width: `${Math.min(100, share)}%` }} />
                </div>

                <ul className="coach-actions">
                  {tip.actions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
